import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import CSSModules from "react-css-modules";
import css from "./styles.scss";
import StyleWrapper from "components/Global/Modal/Layout/StyleWrapper";
import { chunk } from "lodash";
import Loading from "components/Global/Modal/Layout/Loading";
import Footer from "./Footer";
import Send from "./Scenes/Send";
import { hideModal } from "redux/modules/modal/actions";
import PortalUserApi from "redux/modules/portal/users/api";
import { connect } from "react-redux";
import { getCredentials } from "redux/modules/user/selectors";
import * as R from "ramda";
import ManagePortalAccess from "./Scenes/ManagePortalAccess";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

const decorate = connect(
  state => ({
    credentials: getCredentials(state)
  }),
  {
    hideModal
  }
);

const CONTACT_PORTAL_COPY =
  "Welcome to your portal. You are one step closer to being a part of an amazing event. \n" +
  "\n" +
  "Let's help you get logged in to complete tasks, submit files, and view important information about the upcoming event.\n" +
  "\n" +
  "Click the button below to log in with your email address. If you already have a Sessionboard password, use it to sign in. Otherwise, you'll be prompted to set one. Or, If your organization uses Single Sign-On (SSO), you can sign in with those credentials.";

const GROUP_PORTAL_COPY =
  "Welcome to your portal. You are one step closer to being a part of an amazing event. \n" +
  "\n" +
  "Let's help you get logged in to complete tasks, submit files, and view important information about the upcoming event.\n" +
  "\n" +
  "Click the button below to log in with your email address. If you already have a Sessionboard password, use it to sign in. Otherwise, you'll be prompted to set one. Or, If your organization uses Single Sign-On (SSO), you can sign in with those credentials.\n" +
  "\n" +
  "If you have another portal for this event, you can choose which one to access while logging in.";

const LoginDetailsModal = ({
  credentials,
  hideModal,
  eventDetails,
  contacts,
  createPortalUsersFromContacts,
  onDone: refreshTableData,
  sendEmails,
  showSnackbar,
  router,
  moduleId
}) => {
  const [state, setState] = useState({
    copied: false,
    message: "",
    loading: true,
    savingPortalAccess: false,
    sendingEmails: false,
    currentScene: 0,
    showConfirmation: false,
    savingPortalAccessForContacts: [],
    portalUserCreationResult: {
      valid: [],
      invalid_emails: [],
      already_has_access: []
    }
  });

  const {
    currentScene,
    loading,
    portalUserCreationResult,
    message,
    showConfirmation
  } = state;

  const textAreaRef = useRef(null);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        const { id: eventId } = eventDetails;
        const portalUserCreationResult = await createPortalUsersFromContacts({
          moduleId,
          eventId,
          contacts,
          signal: abortController.signal // Pass signal to the fetch function
        });
        if (!abortController.signal.aborted) {
          const records = portalUserCreationResult.valid.concat(
            portalUserCreationResult.already_has_access
          );
          const isGroupPortal = records.length
            ? Object.keys(records[0]).includes("account_id")
            : false;
          // Check if the fetch was aborted before updating state
          setState({
            ...state,
            loading: false,
            portalUserCreationResult,
            message: isGroupPortal ? GROUP_PORTAL_COPY : CONTACT_PORTAL_COPY
          });
        }
      } catch (error) {
        // Handle error
      }
    };

    fetchData();

    return () => {
      // Cleanup function to cancel the fetch operation
      abortController.abort(); // Abort the fetch operation when the component unmounts
    };
  }, [state.currentScene]);

  const onMessageChange = event =>
    setState({ ...state, message: R.path(["target", "value"], event) });

  const getEmailRecipients = () =>
    R.prop(
      showConfirmation ? "valid" : "already_has_access",
      portalUserCreationResult
    );

  const removeAccess = async userId => {
    setState({ ...state, loading: true });
    const { id: eventId } = eventDetails;
    await PortalUserApi.delete(credentials, {
      eventId,
      userId
    });

    refreshTableData();
    const portalUserCreationResult = await createPortalUsersFromContacts({
      moduleId,
      eventId,
      contacts
    });

    const records = portalUserCreationResult.valid.concat(
      portalUserCreationResult.already_has_access
    );
    const isGroupPortal = records.length
      ? Object.keys(records[0]).includes("account_id")
      : false;

    setState({
      ...state,
      loading: false,
      portalUserCreationResult,
      message: isGroupPortal ? GROUP_PORTAL_COPY : CONTACT_PORTAL_COPY
    });
  };

  const goToContactProfile = contactId => {
    const { id: eventId } = eventDetails;
    router.push({
      pathname: `/event/${eventId}/contact/${contactId}`
    });
    hideModal();
  };

  const goToAccountProfile = accountId => {
    const { id: eventId } = eventDetails;
    router.push({
      pathname: `/event/${eventId}/account/${accountId}`
    });
    hideModal();
  };

  const handleCopy = async () => {
    textAreaRef.current.selectText();
    setState({ ...state, copied: true });
    setTimeout(() => setState({ ...state, copied: false }), 2500);
  };

  const changeStep = direction =>
    setState({
      ...state,
      savingPortalAccessForContacts: [],
      showConfirmation: false,
      currentScene: direction === "forward" ? 1 : 0
    });

  const handleAfterGivingAccess = contactIds => {
    setState({
      ...state,
      showConfirmation: true,
      savingPortalAccessForContacts: contactIds,
      currentScene: 1
    });
  };

  const giveAccess = async () => {
    if (!R.prop("valid", portalUserCreationResult)) {
      return;
    }

    setState({
      ...state,
      savingPortalAccess: true
    });
    const chunks = chunk(portalUserCreationResult.valid, 25);
    const { id: eventId } = eventDetails;

    for (const chunkContacts of chunks) {
      await createPortalUsersFromContacts({
        moduleId,
        eventId,
        contacts: chunkContacts.map(c => ({
          contactId:
            moduleId === STANDARD_MODULE_IDS.accounts.id ? c.account_id : c.id
        })),
        save: true
      });
    }

    if (refreshTableData) {
      refreshTableData();
    }

    setState({
      ...state,
      showConfirmation: true,
      savingPortalAccess: false
    });

    handleAfterGivingAccess([...portalUserCreationResult.valid]);
  };

  const sendNotificationEmails = () => {
    const {
      id: eventId,
      name: event_name,
      slug: event_slug,
      uuid: portal_id
    } = eventDetails;
    const records =
      state.savingPortalAccessForContacts &&
      state.savingPortalAccessForContacts.length
        ? state.savingPortalAccessForContacts
        : getEmailRecipients();

    return sendEmails(
      eventId,
      records.map(record => {
        const { account: account_name, email: user_email } = record;
        const variables = {
          message,
          event_name,
          event_slug,
          portal_id,
          account_name,
          user_email
        };
        return {
          eventId,
          templateName: "notification-account-invite",
          recipients: [{ email: user_email }],
          variables: Object.keys(variables).map(key => ({
            name: key,
            content: variables[key]
          }))
        };
      })
    );
  };

  const handleSendEmail = async () => {
    setState({ ...state, sending: true });
    const emailRecipients = getEmailRecipients();
    await sendNotificationEmails();
    showSnackbar({
      message: `Email${R.prop("length", emailRecipients) !== 1 ? "s" : ""} sent`
    });
    if (refreshTableData) {
      refreshTableData();
    }
    hideModal();
  };

  const getScene = () => {
    const {
      copied,
      currentScene,
      portalUserCreationResult,
      showConfirmation,
      sendingEmails,
      message
    } = state;
    const { slug, uuid } = eventDetails;
    return currentScene === 0 ? (
      <ManagePortalAccess
        result={portalUserCreationResult}
        removeAccess={removeAccess}
        goToContactProfile={goToContactProfile}
        goToAccountProfile={goToAccountProfile}
        givePortalAccess={giveAccess}
        goToShareScreen={() => changeStep("forward")}
      />
    ) : (
      <Send
        showConfirmation={showConfirmation}
        sending={sendingEmails}
        records={getEmailRecipients()}
        message={message}
        onMessageChange={onMessageChange}
        handleSendEmail={handleSendEmail}
        hideModal={hideModal}
        handleCopy={handleCopy}
        copied={copied}
        registerTextareaRef={ref => {
          textAreaRef.current = ref;
        }}
        url={`${R.prop(
          "__LENND_APP_URL__",
          window
        )}/portal-login/${slug}/${uuid}`}
      />
    );
  };

  return (
    <StyleWrapper
      containerStyles={{
        minHeight: currentScene === 0 ? "85vh" : "503px",
        margin: 0
      }}
      bodyStyles={{ padding: 0 }}
      hideModal={hideModal}
      heading={"Manage Portal Access"}
      width={500}
    >
      {loading ? (
        <div styleName="body">
          <Loading />
        </div>
      ) : (
        getScene()
      )}
      {currentScene === 1 ? (
        <div styleName="footer">
          <Footer
            goToAccessScreen={() => changeStep("back")}
            hideModal={hideModal}
          />
        </div>
      ) : null}
    </StyleWrapper>
  );
};

LoginDetailsModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  eventDetails: PropTypes.shape({
    id: PropTypes.number,
    uuid: PropTypes.string,
    slug: PropTypes.string
  }).isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      contactId: PropTypes.string,
      accountId: PropTypes.string
    })
  ).isRequired,
  createPortalUsersFromContacts: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  sendEmails: PropTypes.func.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  shareOnly: PropTypes.bool
};

export default decorate(CSSModules(LoginDetailsModal, css));
